import _ from "lodash";

// Builds the file name used when a template screenshot / video is downloaded.
//
// Structure:  <Template> Template <Opponent> (H|A) <Format>
// Example:    "Line Up Template Borussia Dortmund (A) 4x5.jpg"
//
// Naming the files this way keeps every asset of a matchday grouped and sorted
// by template and opponent once they land in the download folder.

const FORMAT_LABELS = {
  "1920x1080": "16x9",
  "1200x1500": "4x5",
  "1080x1920": "9x16",
};

const RATIO_LABELS = {
  1.78: "16x9",
  0.8: "4x5",
  0.56: "9x16",
};

// "/" is a valid character in template names ("Slider 1/4") but not in file
// names, so it becomes a dash. The rest are characters Windows/macOS reject.
const sanitize = (value) =>
  String(value || "")
    .replace(/[\\/]/g, "-")
    .replace(/[<>:"|?*\x00-\x1f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

export const getFormatLabel = (template, format) =>
  FORMAT_LABELS[format] ||
  RATIO_LABELS[_.get(template, "data.editData.effects.ratio")] ||
  FORMAT_LABELS[_.get(template, "currentFormat")] ||
  // Unknown format: keep it in the name anyway so the files stay distinct.
  format ||
  "";

// Opponent and home/away only make sense when the event tells us which of the
// two teams is ours. Tournament templates without a selected team fall back to
// the plain template name.
export const getMatchNameParts = (template) => {
  const event = _.get(template, "event") || {};
  const selectedTeamId = event.selectedTeamId || event.teamId;
  const home = _.get(event, "home");
  const away = _.get(event, "away");
  const homeId = _.get(home, "id", event.homeTeamId);
  const awayId = _.get(away, "id", event.awayTeamId);

  if (!selectedTeamId || !homeId || !awayId) {
    return {};
  }

  if (String(selectedTeamId) === String(homeId)) {
    return { opponent: _.get(away, "name"), side: "(H)" };
  }

  if (String(selectedTeamId) === String(awayId)) {
    return { opponent: _.get(home, "name"), side: "(A)" };
  }

  return {};
};

export const buildTemplateFileName = ({
  template,
  format,
  extension = "jpg",
}) => {
  const templateName = sanitize(_.get(template, "name", "").replace(/-/g, " "));
  const { opponent, side } = getMatchNameParts(template);
  const formatLabel = getFormatLabel(template, format);

  const parts = [];

  if (templateName) {
    parts.push(`${templateName} Template`);
  }

  if (opponent && side) {
    parts.push(sanitize(opponent), side);
  }

  if (formatLabel) {
    parts.push(formatLabel);
  }

  const name = parts.join(" ").trim() || sanitize(_.get(template, "name")) || "template";

  return `${name}.${extension}`;
};

// Zip archives can hold the same template twice (same name, same format, same
// language), so the repeats get numbered instead of overwriting each other.
export const dedupeFileName = (fileName, taken) => {
  if (!taken.has(fileName)) {
    taken.add(fileName);
    return fileName;
  }

  const dot = fileName.lastIndexOf(".");
  const base = dot === -1 ? fileName : fileName.slice(0, dot);
  const extension = dot === -1 ? "" : fileName.slice(dot);

  let counter = 2;
  while (taken.has(`${base} (${counter})${extension}`)) {
    counter += 1;
  }

  const unique = `${base} (${counter})${extension}`;
  taken.add(unique);
  return unique;
};

// The archive follows the same convention as the files inside it, only without
// a template name:  "Templates Borussia Dortmund (A) EN.zip"
export const buildArchiveFileName = ({
  templates = [],
  lang,
  extension = "zip",
}) => {
  const { opponent, side } =
    templates
      .map(getMatchNameParts)
      .find((parts) => parts.opponent && parts.side) || {};

  const parts = ["Templates"];

  if (opponent && side) {
    parts.push(sanitize(opponent), side);
  }

  if (lang) {
    parts.push(sanitize(lang).toUpperCase());
  }

  return `${parts.join(" ")}.${extension}`;
};

// Ready to append to a screenshot / video url as a query string.
export const templateDownloadQuery = (options) =>
  `action=download&filename=${encodeURIComponent(
    buildTemplateFileName(options),
  )}`;

export default buildTemplateFileName;
